import { App } from 'antd';
import { useEffect } from 'react';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { RingTableFilterData } from 'business/production-and-performance/pages/ring-table';
import { ProdAndPerfRangeFilters } from 'business/production-and-performance/providers/prod-and-perf-filters-provider/types';
import { TableParamsProps } from 'business/providers/table-params';
import {
  Module_Enum,
  Parameter_Ring_Bool_Exp,
  useGetRingsQuery,
} from 'generated/graphql';
import { FilterDataSetter } from 'technical/filter-data';
import { buildParameterRingDateOrFilter } from 'technical/graphql/parameter-ring-date-filter';
import { SORT_ORDER } from 'technical/graphql/sort-order';
import { validateRingRange } from 'technical/validation/rules';

export const useGetFilteredAndPaginatedRingsQuery = (
  constructionSiteId: string,
  tableParams: TableParamsProps,
  filter: ProdAndPerfRangeFilters,
  setFilterData: FilterDataSetter<RingTableFilterData>,
) => {
  const { message } = App.useApp();
  const headers = useGraphqlApiHeaders(Module_Enum.ProdPerf);

  const combinedFilters: Parameter_Ring_Bool_Exp = {};

  if (
    filter.filterType === 'ring' &&
    filter.ringRange &&
    validateRingRange(filter.ringRange)
  ) {
    const [min, max] = filter.ringRange;
    combinedFilters.ringNumber = {
      _gte: min === null ? undefined : min,
      _lte: max === null ? undefined : max,
    };
  }

  if (filter.filterType === 'date' && filter.dateRange) {
    combinedFilters._or = buildParameterRingDateOrFilter(filter.dateRange);
  }

  const { data, loading, error } = useGetRingsQuery({
    variables: {
      constructionSiteId,
      limit: tableParams.pageSize,
      offset: (tableParams.current - 1) * tableParams.pageSize,
      filters: combinedFilters,
      orderBy:
        tableParams.sortField && tableParams.sortOrder
          ? {
              [tableParams.sortField.toString()]:
                SORT_ORDER[tableParams.sortOrder],
            }
          : undefined,
    },
    context: {
      headers,
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (res) => {
      setFilterData({
        selectedRings: res.parameter_ring_aggregate.aggregate?.count,
      });
    },
  });

  useEffect(() => {
    if (error) {
      message.open({
        type: 'error',
        content: error.message,
      });
    }
  }, [error, message]);

  return { data, loading, error };
};
